/**
 * @file useLoadingSkeleton.ts
 * @brief Should a list show its loading skeleton? Only if it had items.
 *
 * @details
 * DES (01-10-2026): "loading per loading skeletons hoo, if there are cards,
 * if not then empty state". Whether a list has items is only known after the
 * fetch, so the browser remembers the last answer per user and list:
 * skeletons while loading when it had items last time (or has never been
 * seen), nothing when it was empty. An empty account then goes straight to
 * its empty state instead of seeing skeletons turn into it on every visit.
 * Storage blocked: skeletons, the common case.
 *
 * Web-only. The phone shows lists blank until the fetch (28-09-2026).
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect } from "react";
import { useAuth } from "@/shared/auth/auth-context";

/**
 * @param list stable name, e.g. "notes" or "home.meetings"
 * @param loading true while the first fetch is in flight
 * @param count items once loaded (undefined until then)
 * @returns true while the list should show its skeleton
 */
export function useLoadingSkeleton(
  list: string,
  loading: boolean,
  count: number | undefined,
): boolean {
  const { user } = useAuth();
  const key = `verblizr.hadItems.${user?.id ?? ""}.${list}`;
  useEffect(() => {
    if (loading || count === undefined) return;
    try {
      localStorage.setItem(key, count > 0 ? "1" : "0");
    } catch {
      /* storage blocked: nothing to remember */
    }
  }, [key, loading, count]);
  if (!loading) return false;
  try {
    return localStorage.getItem(key) !== "0";
  } catch {
    return true;
  }
}
